/*
 * Code generated by Speakeasy (https://speakeasy.com). DO NOT EDIT.
 */

import {
  InvalidateQueryFilters,
  QueryClient,
  useQuery,
  UseQueryResult,
  useSuspenseQuery,
  UseSuspenseQueryResult,
} from "@tanstack/react-query";
import { GramError } from "../models/errors/gramerror.js";
import {
  ConnectionError,
  InvalidRequestError,
  RequestAbortedError,
  RequestTimeoutError,
  UnexpectedClientError,
} from "../models/errors/httpclienterrors.js";
import { ResponseValidationError } from "../models/errors/responsevalidationerror.js";
import { SDKValidationError } from "../models/errors/sdkvalidationerror.js";
import { ServiceError } from "../models/errors/serviceerror.js";
import {
  GetOrganizationRemoteSessionClientDeletePreflightRequest,
  GetOrganizationRemoteSessionClientDeletePreflightSecurity,
} from "../models/operations/getorganizationremotesessionclientdeletepreflight.js";
import { useGramContext } from "./_context.js";
import {
  QueryHookOptions,
  SuspenseQueryHookOptions,
  TupleToPrefixes,
} from "./_types.js";
import {
  buildOrganizationRemoteSessionClientDeletePreflightQuery,
  OrganizationRemoteSessionClientDeletePreflightQueryData,
  prefetchOrganizationRemoteSessionClientDeletePreflight,
  queryKeyOrganizationRemoteSessionClientDeletePreflight,
} from "./organizationRemoteSessionClientDeletePreflight.core.js";
export {
  buildOrganizationRemoteSessionClientDeletePreflightQuery,
  type OrganizationRemoteSessionClientDeletePreflightQueryData,
  prefetchOrganizationRemoteSessionClientDeletePreflight,
  queryKeyOrganizationRemoteSessionClientDeletePreflight,
};

export type OrganizationRemoteSessionClientDeletePreflightQueryError =
  | ServiceError
  | GramError
  | ResponseValidationError
  | ConnectionError
  | RequestAbortedError
  | RequestTimeoutError
  | InvalidRequestError
  | UnexpectedClientError
  | SDKValidationError;

/**
 * getClientDeletePreflight organizationRemoteSessionClients
 *
 * @remarks
 * Authoritative impact summary for deleting a remote_session_client: associated session count, affected MCP server names, and trusted identity-provider login references that must be explicitly unlinked before deletion. Requires org:read.
 */
export function useOrganizationRemoteSessionClientDeletePreflight(
  request: GetOrganizationRemoteSessionClientDeletePreflightRequest,
  security?:
    | GetOrganizationRemoteSessionClientDeletePreflightSecurity
    | undefined,
  options?: QueryHookOptions<
    OrganizationRemoteSessionClientDeletePreflightQueryData,
    OrganizationRemoteSessionClientDeletePreflightQueryError
  >,
): UseQueryResult<
  OrganizationRemoteSessionClientDeletePreflightQueryData,
  OrganizationRemoteSessionClientDeletePreflightQueryError
> {
  const client = useGramContext();
  return useQuery({
    ...buildOrganizationRemoteSessionClientDeletePreflightQuery(
      client,
      request,
      security,
      options,
    ),
    ...options,
  });
}

/**
 * getClientDeletePreflight organizationRemoteSessionClients
 *
 * @remarks
 * Authoritative impact summary for deleting a remote_session_client: associated session count, affected MCP server names, and trusted identity-provider login references that must be explicitly unlinked before deletion. Requires org:read.
 */
export function useOrganizationRemoteSessionClientDeletePreflightSuspense(
  request: GetOrganizationRemoteSessionClientDeletePreflightRequest,
  security?:
    | GetOrganizationRemoteSessionClientDeletePreflightSecurity
    | undefined,
  options?: SuspenseQueryHookOptions<
    OrganizationRemoteSessionClientDeletePreflightQueryData,
    OrganizationRemoteSessionClientDeletePreflightQueryError
  >,
): UseSuspenseQueryResult<
  OrganizationRemoteSessionClientDeletePreflightQueryData,
  OrganizationRemoteSessionClientDeletePreflightQueryError
> {
  const client = useGramContext();
  return useSuspenseQuery({
    ...buildOrganizationRemoteSessionClientDeletePreflightQuery(
      client,
      request,
      security,
      options,
    ),
    ...options,
  });
}

export function setOrganizationRemoteSessionClientDeletePreflightData(
  client: QueryClient,
  queryKeyBase: [
    parameters: {
      id: string;
      gramSession?: string | undefined;
      gramKey?: string | undefined;
    },
  ],
  data: OrganizationRemoteSessionClientDeletePreflightQueryData,
): OrganizationRemoteSessionClientDeletePreflightQueryData | undefined {
  const key = queryKeyOrganizationRemoteSessionClientDeletePreflight(
    ...queryKeyBase,
  );

  return client.setQueryData<
    OrganizationRemoteSessionClientDeletePreflightQueryData
  >(key, data);
}

export function invalidateOrganizationRemoteSessionClientDeletePreflight(
  client: QueryClient,
  queryKeyBase: TupleToPrefixes<
    [parameters: {
      id: string;
      gramSession?: string | undefined;
      gramKey?: string | undefined;
    }]
  >,
  filters?: Omit<InvalidateQueryFilters, "queryKey" | "predicate" | "exact">,
): Promise<void> {
  return client.invalidateQueries({
    ...filters,
    queryKey: [
      "@gram/client",
      "organizationRemoteSessionClients",
      "getDeletePreflight",
      ...queryKeyBase,
    ],
  });
}

export function invalidateAllOrganizationRemoteSessionClientDeletePreflight(
  client: QueryClient,
  filters?: Omit<InvalidateQueryFilters, "queryKey" | "predicate" | "exact">,
): Promise<void> {
  return client.invalidateQueries({
    ...filters,
    queryKey: [
      "@gram/client",
      "organizationRemoteSessionClients",
      "getDeletePreflight",
    ],
  });
}
